import { Grid } from '../common/Grid';
import { RotatedBoundingBox } from '../common/RotatedBoundingBox';
import { ContourTexture } from './ContourTexture';
import * as fflate from 'fflate';
class UndoObject {
    constructor(
        public readonly grid: Grid,
        public readonly boundingBox: RotatedBoundingBox,
        public readonly pixels: Uint8Array,
        public readonly label: string
    ) {}
}

/**
 * A stack of signed distance field pixel diffs that can be used to undo and redo operations. \
 */
export class UndoStack {
    private _stack: UndoObject[] = [];
    private _undoPointer = 0; // direct index of the next state to be undone

    private static MaxStackSize = 10;

    public push(
        texture: ContourTexture,
        changedPixels: Uint8Array[],
        label: string
    ): void {
        if (this._undoPointer < this._stack.length - 1) {
            this._stack = this._stack.slice(0, this._undoPointer + 1);
        }

        const concatBuffer = new Uint8Array(
            changedPixels.length * changedPixels[0].length
        );

        for (let i = 0; i < changedPixels.length; i++) {
            concatBuffer.set(changedPixels[i], i * changedPixels[i].length);
        }

        this._stack.push(
            new UndoObject(
                texture.textureGrid,
                texture.fullBoundingBox,
                fflate.compressSync(concatBuffer, { level: 1 }),
                label
            )
        );
        if (this._stack.length > UndoStack.MaxStackSize) {
            // keep the stack at a reasonable size, but preserve the first state
            this._stack[0] = this._stack.shift()!;
        }
        this._undoPointer = this._stack.length - 1;
    }

    public undo(contourTexture: ContourTexture): void {
        if (this._stack.length === 0 || this._undoPointer <= 0) {
            return undefined;
        }
        this._undoPointer--;
        const undoObj = this._stack[this._undoPointer];
        this.updateTexture(contourTexture, undoObj);
    }

    public redo(contourTexture: ContourTexture): void {
        if (this._undoPointer >= this._stack.length - 1) {
            return undefined;
        }
        this._undoPointer++;
        const redoObj = this._stack[this._undoPointer];
        this.updateTexture(contourTexture, redoObj);
    }

    private updateTexture(
        contourTexture: ContourTexture,
        undoObject: UndoObject
    ) {
        const decompressedPixels = fflate.decompressSync(undoObject.pixels);
        const slices = [];
        const sliceSize = undoObject.grid.sizeX * undoObject.grid.sizeY;
        for (let z = 0; z < undoObject.grid.sizeZ; z++) {
            slices.push(
                decompressedPixels.slice(z * sliceSize, (z + 1) * sliceSize)
            );
        }
        contourTexture.resetFromPixels(
            undoObject.grid,
            undoObject.boundingBox,
            slices
        );
    }

    public clear(): void {
        this._stack = [];
        this._undoPointer = 0;
    }

    public get size(): number {
        return this._stack.length;
    }

    public topLabel(): string | undefined {
        return this._stack[this._stack.length - 1]?.label;
    }

    public canUndo(): boolean {
        return this._undoPointer > 0;
    }

    public canRedo(): boolean {
        return this._undoPointer < this._stack.length - 1;
    }
}
